import { useEffect, useState } from "react";
import moment from "moment";
import { incidentService, userService } from "@iso/services";

const TERMINAL_STATUSES = ["in_progress", "completed"];

export const isTerminalCAStatus = (status) =>
  TERMINAL_STATUSES.includes(status);

export const CA_STATUS_LABELS = {
  assigned: "Assigned",
  in_progress: "In Progress",
  completed: "Completed",
};

export const CA_STATUS_COLORS = {
  assigned: "default",
  in_progress: "processing",
  completed: "success",
};

export const todayISO = () => moment().format("YYYY-MM-DD");

// Approval-time validator: every CA with a description must have an assignee
// and a future-or-today due date. Run when Safety Admin clicks Approve.
export const validateCorrectiveActions = (actions) => {
  if (!Array.isArray(actions)) return null;
  const today = todayISO();
  for (let i = 0; i < actions.length; i++) {
    const ca = actions[i] || {};
    const description = (ca.action || "").trim();
    if (!description) continue;
    if (!ca.assignedTo) {
      return `Corrective action #${i + 1}: please pick an assignee.`;
    }
    if (!ca.dueDate) {
      return `Corrective action #${i + 1}: please set a due date.`;
    }
    if (ca.dueDate < today) {
      return `Corrective action #${i + 1}: due date cannot be in the past.`;
    }
  }
  return null;
};

// Submit-time validator (investigator): only descriptions are required. The
// approver fills in assignee + due date at the approval step.
export const validateCorrectiveActionDescriptions = (actions) => {
  if (!Array.isArray(actions)) return null;
  for (let i = 0; i < actions.length; i++) {
    const ca = actions[i] || {};
    const description = (ca.action || "").trim();
    if (!description) {
      return `Corrective action #${i + 1}: please add a description or remove the row.`;
    }
  }
  return null;
};

/**
 * Loads the property staff list and the existing persisted corrective actions
 * so the form can render the assignee dropdown and decide which rows are
 * locked from removal (work has already started).
 */
const useCorrectiveActionContext = (incidentId, propertyId) => {
  const [staff, setStaff] = useState([]);
  const [persistedActions, setPersistedActions] = useState([]);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    userService
      .getUserForProperty(propertyId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.users)
            ? res.users
            : Array.isArray(res)
              ? res
              : [];
        setStaff(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;
    incidentService.getCorrectiveActions(incidentId).then((res) => {
      if (cancelled) return;
      setPersistedActions(Array.isArray(res) ? res : res?.data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [incidentId]);

  const persistedById = persistedActions.reduce((acc, a) => {
    if (a && a.id != null) acc[a.id] = a;
    return acc;
  }, {});

  const isCALocked = (ca) => {
    if (!ca || ca.id == null) return false;
    const persisted = persistedById[ca.id];
    return !!persisted && isTerminalCAStatus(persisted.status);
  };

  /**
   * Overlays the live corrective action row onto the investigation's stored
   * JSON snapshot. Rows only exist once the investigation is approved (and the
   * form is read-only from then on), so the table is the source of truth for
   * anything that has been reallocated, rescheduled or completed in the
   * Corrective Actions module. Returns the snapshot untouched for draft rows.
   */
  const getLiveCA = (ca) => {
    const persisted = ca && ca.id != null ? persistedById[ca.id] : null;
    if (!persisted) return { ...ca, persisted: null };
    return {
      ...ca,
      action: persisted.description || ca.action,
      assignedTo: persisted.assigned_to || ca.assignedTo,
      dueDate: persisted.due_date || ca.dueDate,
      persisted,
    };
  };

  return { staff, persistedActions, persistedById, isCALocked, getLiveCA };
};

export default useCorrectiveActionContext;
